import { useState, type ReactElement } from 'react';
import type { SchemeDto } from '@gr/core/distribution/scheduling/dto';
import { useServices, useT } from '../../../app/di';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Card } from '../../../ui-kit/Card/Card';
import { useConfirm } from '../../../ui-kit/ConfirmDialog/ConfirmDialog';
import { Dialog } from '../../../ui-kit/Dialog/Dialog';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { useBlog } from '../../accounts/workspaces';
import { SchemeForm } from './SchemeForm/SchemeForm';
import { SchemeList } from './SchemeList/SchemeList';

// SCH-UC-1, SCH-UC-1.CC-1
export function SchemesSection(): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const { blog } = useBlog();
  const confirm = useConfirm();
  const schemes = useResource(() => api.scheduling.schemes(blog.id), [api, blog.id]);
  const channels = useResource(() => api.channels.list(blog.id), [api, blog.id]);
  const [editing, setEditing] = useState<SchemeDto | 'new' | null>(null);
  const remove = useAction((schemeId: string) => api.scheduling.deleteScheme(blog.id, schemeId));
  const activeChannels = (channels.data ?? []).filter((c) => c.active);
  const editingScheme = editing === 'new' ? null : editing;

  return (
    <Card
      title={t('scheduling.schemes.title')}
      actions={
        <Button variant="primary" icon="plus" disabled={activeChannels.length === 0} onClick={() => setEditing('new')}>
          {t('scheduling.schemes.add')}
        </Button>
      }
    >
      <div className="u-stack">
        <p className="u-muted u-small">{t('scheduling.schemes.subtitle')}</p>
        {activeChannels.length === 0 && !channels.loading ? <Alert tone="info">{t('scheduling.schemes.needChannels')}</Alert> : null}
        {remove.error ? <Alert tone="danger">{te(remove.error)}</Alert> : null}
        {schemes.loading && !schemes.data ? (
          <Loading label={t('common.loading')} />
        ) : (
          <SchemeList
            schemes={schemes.data ?? []}
            channels={channels.data ?? []}
            pending={remove.pending}
            onEdit={setEditing}
            onDelete={async (scheme) => {
              if (!(await confirm({ title: t('scheduling.schemes.deleteConfirm', { name: scheme.name }), confirmLabel: t('common.delete'), danger: true }))) return;
              if ((await remove.run(scheme.id)).success) await schemes.reload();
            }}
          />
        )}
      </div>
      {editing ? (
        <Dialog
          open
          title={editingScheme ? t('scheduling.schemes.edit') : t('scheduling.schemes.add')}
          closeLabel={t('common.close')}
          onClose={() => setEditing(null)}
          footer={
            <>
              <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
              <Button type="submit" form="scheme-form" variant="primary">
                {t('common.save')}
              </Button>
            </>
          }
        >
          <SchemeForm
            id="scheme-form"
            scheme={editingScheme}
            channels={activeChannels}
            onSubmit={async (input) => {
              const result = editingScheme ? await api.scheduling.updateScheme(blog.id, editingScheme.id, input) : await api.scheduling.createScheme(blog.id, input);
              if (result.success) {
                setEditing(null);
                await schemes.reload();
              }
              return result;
            }}
          />
        </Dialog>
      ) : null}
    </Card>
  );
}
